import { useEffect, useState } from "react";
import { Radio, ExternalLink, AlertTriangle, Clock } from "lucide-react";
import api from "../../lib/api";

/** Live Threat Brief — CISA KEV feed + Sentinel summary. */
export default function ThreatBriefWidget() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/threat-brief/today");
        if (!cancelled) setBrief(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flat-card p-6">
        <div className="overline" style={{ color: "var(--cyan)" }}>// SENTINEL_BRIEF</div>
        <div className="font-mono text-xs text-[var(--text-2)] mt-2">// Tuning to live threat feeds…</div>
      </div>
    );
  }
  if (!brief || !brief.summary) return null;

  const generatedAgo = brief.generated_at ? timeAgo(brief.generated_at) : "—";

  return (
    <div className="flat-card p-6 scanlines border-l-4 border-[var(--cyan)]" data-testid="threat-brief-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio size={14} color="var(--cyan)" strokeWidth={1.5} className="animate-pulse" />
          <div className="overline" style={{ color: "var(--cyan)" }}>// SENTINEL_BRIEF · TODAY</div>
        </div>
        <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] inline-flex items-center gap-1">
          <Clock size={10} /> {generatedAgo}
        </span>
      </div>

      <p className="text-[var(--text)] text-base leading-relaxed mb-4" data-testid="threat-brief-summary">
        {brief.summary}
      </p>

      {brief.entries?.length > 0 && (
        <div className="border-t border-[var(--border)] pt-3">
          <div className="overline text-[var(--text-2)] mb-2">// TOP_KEV_ENTRIES · {brief.entries.length}</div>
          <div className="space-y-1.5">
            {brief.entries.slice(0, 4).map((e) => (
              <a
                key={e.cve}
                href={`https://nvd.nist.gov/vuln/detail/${e.cve}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between text-xs font-mono text-[var(--text-2)] hover:text-[var(--cyan)] group"
                data-testid={`kev-${e.cve}`}
              >
                <span className="truncate">
                  {e.known_ransomware_use === "Known" && (
                    <AlertTriangle size={10} className="inline mb-0.5 mr-1" color="var(--red)" strokeWidth={1.5} />
                  )}
                  <span className="text-[var(--cyan)]">{e.cve}</span>
                  <span className="mx-1 text-[var(--text-2)]">·</span>
                  {e.vendor} {e.product}
                </span>
                <ExternalLink size={10} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--text-2)] mt-3">
        SOURCE · CISA KEV {brief.catalog_version ? `v${brief.catalog_version}` : ""}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const then = new Date(iso).getTime();
    const diffMin = Math.round((Date.now() - then) / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return `${Math.round(diffMin / (60 * 24))}d ago`;
  } catch {
    return "—";
  }
}
